import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  Warning,
  CheckCircle,
  ExpandMore,
  Assessment,
  MonetizationOn,
  People,
  Inventory,
  Timeline,
  Security,
  Lightbulb,
  SmartToy,
  Refresh,
  Psychology,
  AutoGraph,
} from '@mui/icons-material';
import backendApi from '../shared/api/backendApi';

function BusinessIntelligence() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({
    stage: '',
    progress: 0,
    message: 'Initializing analysis...'
  });
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [businessInsights, setBusinessInsights] = useState({
    lowStockItems: [],
    reorderSuggestions: [],
    alerts: [],
    personalizedInsights: {
      trendingProducts: [],
      topCustomers: [],
      demandPatterns: [],
      proactiveReorderSuggestions: [],
      productCombinations: { combinations: [], pairs: [], insights: [] },
      priceOptimization: [],
      bundleRecommendations: [],
      crossSellingOpportunities: [],
      dealerAnalysis: [],
      lowStockAlerts: []
    },
    comprehensiveInsights: {
      businessHealth: {
        status: 'NOT_LOADED',
        score: null,
        factors: {},
        trends: {},
        alerts: []
      },
      strategicRecommendations: [],
      riskAssessments: [],
      growthOpportunities: [],
      keyPerformanceIndicators: {}
    }
  });
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [businessMetrics, setBusinessMetrics] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    profitMargin: 0,
    inventoryTurnover: 0,
    customerRetention: 0,
    averageOrderValue: 0,
    cashFlow: 0
  });

  useEffect(() => {
    // Check for existing data in localStorage and load it automatically
    const loadExistingData = () => {
      try {
        const savedData = localStorage.getItem('businessAnalyticsData');
        const savedTimestamp = localStorage.getItem('businessAnalyticsTimestamp');

        if (savedData && savedTimestamp) {
          const data = JSON.parse(savedData);
          const timestamp = parseInt(savedTimestamp);
          const now = Date.now();
          const hoursSinceUpdate = (now - timestamp) / (1000 * 60 * 60);

          // Load data if it's less than 24 hours old
          if (hoursSinceUpdate < 24) {
            console.log('📊 Business Intelligence: Loading existing data from localStorage');

            // Extract data from the correct structure
            const overviewData = data.overview?.overview || data.overview || {};
            const crossSheetInsights = overviewData.crossSheetInsights || {};
            const personalizedInsights = crossSheetInsights.personalizedInsights || {};

            // Extract comprehensive business insights
            const comprehensiveInsights = overviewData.comprehensiveInsights || {
              businessHealth: { status: 'UNKNOWN', score: 0 },
              strategicRecommendations: [],
              riskAssessments: [],
              growthOpportunities: [],
              keyPerformanceIndicators: {}
            };

            // Transform the data to match expected format
            const transformedData = {
              businessName: overviewData.businessName || 'Business',
              sheetId: overviewData.sheetId || '',
              generatedAt: overviewData.generatedAt || new Date(timestamp).toISOString(),
              comprehensiveInsights,
              crossSheetInsights: crossSheetInsights
            };

            setInsights(transformedData);

            // Update businessInsights state for immediate display
            setBusinessInsights(prevState => ({
              ...prevState,
              lowStockItems: personalizedInsights.lowStockAlerts || [],
              reorderSuggestions: personalizedInsights.proactiveReorderSuggestions || [],
              alerts: crossSheetInsights.alerts || [],
              personalizedInsights: {
                trendingProducts: personalizedInsights.trendingProducts || [],
                topCustomers: personalizedInsights.topCustomers || [],
                demandPatterns: personalizedInsights.demandPatterns || [],
                proactiveReorderSuggestions: personalizedInsights.proactiveReorderSuggestions || [],
                productCombinations: personalizedInsights.productCombinations || { combinations: [], pairs: [], insights: [] },
                priceOptimization: personalizedInsights.priceOptimization || [],
                bundleRecommendations: personalizedInsights.bundleRecommendations || [],
                crossSellingOpportunities: personalizedInsights.crossSellingOpportunities || [],
                dealerAnalysis: personalizedInsights.dealerAnalysis || [],
                lowStockAlerts: personalizedInsights.lowStockAlerts || []
              },
              comprehensiveInsights
            }));

            console.log('📊 Business Intelligence: Existing data loaded successfully');
            console.log('📊 Business Intelligence: Data age:', hoursSinceUpdate.toFixed(1), 'hours');
          } else {
            console.log('📊 Business Intelligence: Existing data is too old (', hoursSinceUpdate.toFixed(1), 'hours), will require fresh data');
          }
        } else {
          console.log('📊 Business Intelligence: No existing data found in localStorage');
        }
      } catch (error) {
        console.error('📊 Business Intelligence: Error loading existing data:', error);
      }
    };

    loadExistingData();
  }, []);


  const generateAIInsights = async () => {
    try {
      setGeneratingInsights(true);

      // Use the new comprehensive business report endpoint (no sheetId needed for database)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/business/report?reportType=comprehensive&includeCharts=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.report) {
          // Update insights with the new comprehensive report
          setInsights({
            ...insights,
            comprehensiveReport: data.report,
            generatedAt: data.generatedAt
          });

          // Show success message
          alert('AI insights generated successfully! Check the new comprehensive report section.');
        } else {
          throw new Error('Invalid report response');
        }
      } else {
        throw new Error(`Report generation failed: ${response.status}`);
      }
    } catch (err) {
      console.error('Error generating AI insights:', err);
      alert('Failed to generate AI insights. Please try again.');
    } finally {
      setGeneratingInsights(false);
    }
  };

  const loadBusinessInsights = async () => {
    try {
      setLoading(true);
      setError(null);

      // Progress stages
      const progressStages = [
        { stage: 'Initializing', message: 'Initializing analysis...', progress: 10 },
        { stage: 'Reading Sales Data', message: 'Analyzing your sales data...', progress: 25 },
        { stage: 'Reading Purchase Data', message: 'Analyzing your purchase data...', progress: 40 },
        { stage: 'Reading Inventory Data', message: 'Analyzing your inventory data...', progress: 55 },
        { stage: 'Reading Customer Data', message: 'Analyzing your customer data...', progress: 70 },
        { stage: 'Processing Insights', message: 'Processing business insights...', progress: 85 },
        { stage: 'Finalizing', message: 'Finalizing analysis...', progress: 95 }
      ];

      let currentStage = 0;

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        if (currentStage < progressStages.length) {
          setLoadingProgress(progressStages[currentStage]);
          currentStage++;
        }
      }, 800);

      // Use centralized backend API client so auth/user/company context is attached consistently
      const data = await backendApi.getAnalyticsOverview();
      if (data.success) {
          console.log('📊 FRONTEND: Received data from backend');
          console.log('📊 FRONTEND: Data keys:', Object.keys(data));
          console.log('📊 FRONTEND: Full data structure:', JSON.stringify(data, null, 2));

          // Extract data from the correct structure - backend sends data in overview.overview format
          const overviewData = data.overview?.overview || data.overview || {};
          const crossSheetInsights = overviewData.crossSheetInsights || {};
          const personalizedInsights = crossSheetInsights.personalizedInsights || {};

          console.log('📊 FRONTEND: overviewData keys:', Object.keys(overviewData));
          console.log('📊 FRONTEND: Extracted crossSheetInsights keys:', Object.keys(crossSheetInsights));
          console.log('📊 FRONTEND: Extracted personalizedInsights keys:', Object.keys(personalizedInsights));
          console.log('📊 FRONTEND: Trending products count:', personalizedInsights.trendingProducts?.length || 0);
          console.log('📊 FRONTEND: Top customers count:', personalizedInsights.topCustomers?.length || 0);
          console.log('📊 FRONTEND: Low stock alerts count:', personalizedInsights.lowStockAlerts?.length || 0);

          // Extract comprehensive business insights
          const comprehensiveInsights = overviewData.comprehensiveInsights || {
            businessHealth: { status: 'UNKNOWN', score: 0 },
            strategicRecommendations: [],
            riskAssessments: [],
            growthOpportunities: [],
            keyPerformanceIndicators: {}
          };

          // Transform the data to match expected format
          const transformedData = {
            businessName: overviewData.businessName || 'Business',
            generatedAt: overviewData.generatedAt || new Date().toISOString(),
            comprehensiveInsights,
            crossSheetInsights: crossSheetInsights
          };

          console.log('📊 FRONTEND: Transformed data structure ready for display');
          setInsights(transformedData);

          // Save to localStorage for Dashboard to use
          localStorage.setItem('businessAnalyticsData', JSON.stringify(data));
          localStorage.setItem('businessAnalyticsTimestamp', Date.now().toString());
          console.log('📊 Business Intelligence: Analytics data saved to localStorage');
          console.log('📊 Business Intelligence: Saved data structure:', Object.keys(data));
          console.log('📊 Business Intelligence: Saved overview structure:', Object.keys(data.overview || {}));
          console.log('📊 Business Intelligence: Saved overview.overview structure:', Object.keys(data.overview?.overview || {}));

          // Debug the business health score data being saved
          const debugOverviewData = data.overview?.overview || data.overview || {};
          const debugCrossSheetInsights = debugOverviewData.crossSheetInsights || {};
          console.log('📊 Business Intelligence: Business health score being saved:');
          console.log('  - debugCrossSheetInsights.keyMetrics?.businessHealthScore:', debugCrossSheetInsights.keyMetrics?.businessHealthScore);
          console.log('  - debugCrossSheetInsights.overallHealthScore:', debugCrossSheetInsights.overallHealthScore);
          console.log('  - debugOverviewData.comprehensiveInsights?.businessHealth?.score:', debugOverviewData.comprehensiveInsights?.businessHealth?.score);
          console.log('  - debugCrossSheetInsights.keyMetrics?.overallHealthScore:', debugCrossSheetInsights.keyMetrics?.overallHealthScore);
          console.log('  - debugCrossSheetInsights.overallHealth:', debugCrossSheetInsights.overallHealth);
          console.log('  - debugOverviewData.comprehensiveInsights?.businessHealth?.status:', debugOverviewData.comprehensiveInsights?.businessHealth?.status);

          // Update businessInsights state for immediate display
          setBusinessInsights(prevState => ({
            ...prevState,
            lowStockItems: personalizedInsights.lowStockAlerts || [],
            reorderSuggestions: personalizedInsights.proactiveReorderSuggestions || [],
            alerts: crossSheetInsights.alerts || [],
            personalizedInsights: {
              trendingProducts: personalizedInsights.trendingProducts || [],
              topCustomers: personalizedInsights.topCustomers || [],
              demandPatterns: personalizedInsights.demandPatterns || [],
              proactiveReorderSuggestions: personalizedInsights.proactiveReorderSuggestions || [],
              productCombinations: personalizedInsights.productCombinations || { combinations: [], pairs: [], insights: [] },
              priceOptimization: personalizedInsights.priceOptimization || [],
              bundleRecommendations: personalizedInsights.bundleRecommendations || [],
              crossSellingOpportunities: personalizedInsights.crossSellingOpportunities || [],
              dealerAnalysis: personalizedInsights.dealerAnalysis || [],
              lowStockAlerts: personalizedInsights.lowStockAlerts || []
            },
            comprehensiveInsights
          }));

          console.log('📊 FRONTEND: Business insights state updated');
          console.log('📊 FRONTEND: Low stock items count:', personalizedInsights.lowStockAlerts?.length || 0);
          console.log('📊 FRONTEND: Trending products count:', personalizedInsights.trendingProducts?.length || 0);
          console.log('📊 FRONTEND: Top customers count:', personalizedInsights.topCustomers?.length || 0);
          console.log('📊 FRONTEND: Reorder suggestions count:', personalizedInsights.proactiveReorderSuggestions?.length || 0);

          // Complete the loading
          clearInterval(progressInterval);
          setLoadingProgress({ stage: 'Complete', message: 'Analysis complete!', progress: 100 });
      } else {
        clearInterval(progressInterval);
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError('Failed to load business insights. Please try again.');
      console.error('Error loading business insights:', err);
    } finally {
      // Ensure loading is turned off after a delay
      setTimeout(() => {
        setLoading(false);
        setLoadingProgress({ stage: '', message: '', progress: 0 });
      }, 1000);
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'EXCELLENT': return 'success';
      case 'GOOD': return 'primary';
      case 'FAIR': return 'warning';
      case 'CONCERNING': return 'orange';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'EXCELLENT':
      case 'GOOD': return <CheckCircle color="success" />;
      case 'FAIR': return <Warning color="warning" />;
      case 'CONCERNING':
      case 'CRITICAL': return <Warning color="error" />;
      default: return <Assessment />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header Section */}
        <Box sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          backdropFilter: 'blur(8px)',
        }}>
          <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 1,
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Business Intelligence Hub
                </Typography>
                <Typography variant="body1" sx={{
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: 600,
                }}>
                  AI-powered comprehensive analysis of your business performance, risks, and opportunities
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Enhanced Loading Section */}
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 8 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            {/* Main Loading Card */}
            <Card sx={{
              mb: 4,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)'
            }}>
              <CardContent sx={{ p: 6 }}>
                {/* Animated Icon */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 4,
                  position: 'relative'
                }}>
                  <Box sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CircularProgress
                      size={80}
                      thickness={4}
                      value={loadingProgress.progress}
                      sx={{
                        color: 'primary.main',
                        position: 'absolute'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem'
                    }}>
                      📊
                    </Box>
                  </Box>
                </Box>

                {/* Progress Text */}
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {loadingProgress.stage}
                </Typography>

                <Typography variant="h6" sx={{ color: 'text.primary', mb: 3, fontWeight: 500 }}>
                  {loadingProgress.message}
                </Typography>

                {/* Progress Bar */}
                <Box sx={{ mb: 4 }}>
                  <LinearProgress
                    variant="determinate"
                    value={loadingProgress.progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'right' }}>
                    {loadingProgress.progress}% Complete
                  </Typography>
                </Box>

                {/* Loading Steps */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  {[
                    { label: 'Reading Sales Data', icon: '💰', progress: loadingProgress.progress >= 25 },
                    { label: 'Reading Purchase Data', icon: '🛒', progress: loadingProgress.progress >= 40 },
                    { label: 'Reading Inventory Data', icon: '📦', progress: loadingProgress.progress >= 55 },
                    { label: 'Reading Customer Data', icon: '👥', progress: loadingProgress.progress >= 70 },
                    { label: 'Processing Insights', icon: '🧠', progress: loadingProgress.progress >= 85 },
                    { label: 'Finalizing Analysis', icon: '✨', progress: loadingProgress.progress >= 95 }
                  ].map((step, index) => (
                    <Box key={index} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: step.progress ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                      border: `1px solid ${step.progress ? 'rgba(76, 175, 80, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                      transition: 'all 0.3s ease'
                    }}>
                      <Typography variant="h6" sx={{ mr: 2 }}>
                        {step.icon}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          color: step.progress ? 'success.main' : 'text.secondary'
                        }}>
                          {step.label}
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: step.progress ? 'success.main' : 'text.secondary'
                        }}>
                          {step.progress ? '✓ Complete' : '⏳ Pending'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Motivational Message */}
                <Box sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: 'rgba(102, 126, 234, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(102, 126, 234, 0.2)'
                }}>
                  <Typography variant="body1" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                    💡 <strong>Analyzing your business data...</strong> This comprehensive analysis will provide valuable insights into your sales performance, customer behavior, inventory optimization, and growth opportunities.
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Fun Facts */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {[
                { icon: '📈', title: 'Real-time Analysis', desc: 'Processing live data from your Google Sheets' },
                { icon: '🤖', title: 'AI-Powered', desc: 'Advanced algorithms analyzing patterns' },
                { icon: '🎯', title: 'Actionable Insights', desc: 'Specific recommendations for your business' }
              ].map((fact, index) => (
                <Card key={index} sx={{
                  textAlign: 'center',
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>{fact.icon}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{fact.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{fact.desc}</Typography>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!insights) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header Section */}
        <Box sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          backdropFilter: 'blur(8px)',
        }}>
          <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 1,
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Business Intelligence Hub
                </Typography>
                <Typography variant="body1" sx={{
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: 600,
                }}>
                  AI-powered comprehensive analysis of your business performance, risks, and opportunities
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 6 }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
              🚀 Ready to Analyze Your Business
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 600, mx: 'auto' }}>
              Click the "Generate AI Insights" button above to fetch and analyze your business data. Get comprehensive insights, AI-powered recommendations, and strategic guidance for your business.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<SmartToy />}
              onClick={() => {
                loadBusinessInsights();
              }}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              Generate AI Insights
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

const { comprehensiveInsights = {}, crossSheetInsights = {} } = insights || {};
const keyMetrics = crossSheetInsights.keyMetrics || {};
const personalizedInsights = businessInsights.personalizedInsights || {};
const healthScore = keyMetrics.businessHealthScore || comprehensiveInsights.businessHealth?.score || 0;
const netProfitValue = keyMetrics.netProfit ||
  comprehensiveInsights.keyPerformanceIndicators?.financialKPIs?.netProfit ||
  0;
const marginValue = keyMetrics.netProfitMargin || keyMetrics.profitMargin || 0;
const collectionRatioValue = Math.max(0, Math.min(keyMetrics.workingCapitalRatio || 0, 1));
const repeatRateValue = Math.max(0, Math.min(keyMetrics.repeatPurchaseRate || 0, 1));
const outstandingRatioPercent = (keyMetrics.customerOutstandingRatio || 0) * 100;
const salesGrowthValue = keyMetrics.salesGrowthRate || 0;
const trendWindowDays = keyMetrics.trendWindowDays || 30;
const grossMarginValue = keyMetrics.grossMargin || keyMetrics.profitMargin || 0;
const inventoryTurnoverValue = keyMetrics.inventoryTurnoverRatio || 0;
const churnRateValue = Math.max(0, Math.min((keyMetrics.churnRate || 0) * 100, 100));
const activeCustomersPerDayValue = keyMetrics.avgCustomersPerDay || 0;
const totalInventoryCountValue = keyMetrics.totalInventoryCount || keyMetrics.totalInventory || 0;
const lowStockItemsValue = keyMetrics.lowStockItems ||
  personalizedInsights.lowStockAlerts?.length ||
  0;

const leadProduct = personalizedInsights.trendingProducts?.[0] || null;
const leadCustomer = personalizedInsights.topCustomers?.[0] || null;
const criticalReorders = (personalizedInsights.proactiveReorderSuggestions || [])
  .filter((item) => item.priority === 'CRITICAL').length;
const reorderSuggestionsCount = personalizedInsights.proactiveReorderSuggestions?.length || 0;
const topProductsList = personalizedInsights.trendingProducts || [];
const topCustomersList = personalizedInsights.topCustomers || [];
const demandPatternsList = personalizedInsights.demandPatterns || [];
const reorderSuggestionsList = personalizedInsights.proactiveReorderSuggestions || [];
const lowStockAlertList = personalizedInsights.lowStockAlerts || [];
const priceOptimizationList = personalizedInsights.priceOptimization || [];
const crossSellOpportunitiesList = personalizedInsights.crossSellingOpportunities || [];
const crossSellLead = personalizedInsights.crossSellingOpportunities?.[0] || null;
const pricingLead = personalizedInsights.priceOptimization?.[0] || null;
const demandLead = personalizedInsights.demandPatterns?.[0] || null;

const assistantQuickPanels = [
  {
    id: 'financial',
    title: 'Financial Engine',
    metric: formatCurrency(netProfitValue),
    sub: `Margin ${marginValue.toFixed(1)}% • Gross ${grossMarginValue.toFixed(1)}%`,
    progress: Math.max(0, Math.min(100, marginValue)),
    tone: netProfitValue >= 0 ? '#22c55e' : '#ef4444'
  },
  {
    id: 'customers',
    title: 'Customer Engine',
    metric: `${(repeatRateValue * 100).toFixed(1)}%`,
    sub: `Repeat rate • Churn ${churnRateValue.toFixed(1)}%`,
    progress: repeatRateValue * 100,
    tone: '#38bdf8'
  },
  {
    id: 'inventory',
    title: 'Inventory Engine',
    metric: `${inventoryTurnoverValue.toFixed(1)}x`,
    sub: `${totalInventoryCountValue} SKUs • ${reorderSuggestionsCount} reorders`,
    progress: Math.max(0, Math.min(100, inventoryTurnoverValue * 12)),
    tone: '#f59e0b'
  }
];

const assistantQueue = [];
if (criticalReorders > 0) {
  assistantQueue.push({
    id: 'reorder',
    title: 'Resolve stockout risks',
    detail: `${criticalReorders} critical items need immediate purchase planning.`,
    priority: 'HIGH',
    icon: 'inventory'
  });
}
if (outstandingRatioPercent > 15) {
  assistantQueue.push({
    id: 'collections',
    title: 'Accelerate payment collection',
    detail: `${outstandingRatioPercent.toFixed(1)}% of revenue is currently outstanding.`,
    priority: 'HIGH',
    icon: 'money'
  });
}
if (salesGrowthValue < 0) {
  assistantQueue.push({
    id: 'sales-growth',
    title: 'Recover sales momentum',
    detail: `Sales trend is ${salesGrowthValue.toFixed(1)}% vs previous period.`,
    priority: 'MEDIUM',
    icon: 'trend'
  });
}
if ((personalizedInsights.crossSellingOpportunities || []).length > 0) {
  const firstCrossSell = personalizedInsights.crossSellingOpportunities[0];
  assistantQueue.push({
    id: 'cross-sell',
    title: 'Activate cross-sell script',
    detail: `Promote ${firstCrossSell.suggestedProduct} when ${firstCrossSell.primaryProduct} is sold.`,
    priority: 'MEDIUM',
    icon: 'customer'
  });
}

const assistantDisplayQueue = assistantQueue.slice(0, 4);

// Debug: Log current state when component renders
console.log('📊 FRONTEND: Business Intelligence component render');
console.log('📊 FRONTEND: Insights available:', !!insights);
console.log('📊 FRONTEND: Business insights available:', !!businessInsights.personalizedInsights);
console.log('📊 FRONTEND: Real data from backend:');
console.log('  - Total Revenue:', crossSheetInsights.keyMetrics?.totalRevenue || 0);
console.log('  - Total Expenses:', crossSheetInsights.keyMetrics?.totalExpenses || 0);
console.log('  - Net Profit:', crossSheetInsights.keyMetrics?.netProfit || 0);
console.log('  - Trending products count:', businessInsights.personalizedInsights?.trendingProducts?.length || 0);
console.log('  - Top customers count:', businessInsights.personalizedInsights?.topCustomers?.length || 0);
console.log('  - Low stock alerts count:', businessInsights.lowStockItems?.length || 0);

 return (
   <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
     {/* Header Section */}
     <Box sx={{
       borderBottom: '1px solid',
       borderColor: 'divider',
       bgcolor: 'background.paper',
       backdropFilter: 'blur(8px)',
     }}>
       <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 4 }}>
         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
           <Box>
             <Typography variant="h3" sx={{
               fontWeight: 800,
               fontSize: { xs: '2rem', md: '2.5rem' },
               mb: 1,
               letterSpacing: '-0.02em',
               background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
               backgroundClip: 'text',
               WebkitBackgroundClip: 'text',
               WebkitTextFillColor: 'transparent',
             }}>
               Business Intelligence Hub
             </Typography>
             <Typography variant="body1" sx={{
               color: 'text.secondary',
               fontSize: '1.1rem',
               maxWidth: 600,
             }}>
               AI-powered comprehensive analysis of your business performance, risks, and opportunities
             </Typography>
           </Box>
           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
             {/* Data freshness indicator */}
             {insights && (
               <Box sx={{ textAlign: 'right' }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
                   <Box sx={{
                     width: 8,
                     height: 8,
                     borderRadius: '50%',
                     backgroundColor: (() => {
                       const savedTimestamp = localStorage.getItem('businessAnalyticsTimestamp');
                       if (savedTimestamp) {
                         const hoursSinceUpdate = (Date.now() - parseInt(savedTimestamp)) / (1000 * 60 * 60);
                         if (hoursSinceUpdate < 2) return 'success.main'; // Green for fresh data
                         if (hoursSinceUpdate < 12) return 'warning.main'; // Yellow for moderately fresh
                         return 'error.main'; // Red for old data
                       }
                       return 'grey.400';
                     })()
                   }} />
                   <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                     {(() => {
                       const savedTimestamp = localStorage.getItem('businessAnalyticsTimestamp');
                       if (savedTimestamp) {
                         const hoursSinceUpdate = (Date.now() - parseInt(savedTimestamp)) / (1000 * 60 * 60);
                         if (hoursSinceUpdate < 2) return 'Fresh Data';
                         if (hoursSinceUpdate < 12) return 'Recent Data';
                         return 'Outdated Data';
                       }
                       return 'No Data';
                     })()}
                   </Typography>
                 </Box>
                 <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                   Last updated: {new Date(insights.generatedAt || Date.now()).toLocaleString('en-IN', {
                     year: 'numeric',
                     month: 'short',
                     day: 'numeric',
                     hour: '2-digit',
                     minute: '2-digit'
                   })}
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                   {(() => {
                     const savedTimestamp = localStorage.getItem('businessAnalyticsTimestamp');
                     if (savedTimestamp) {
                       const hoursSinceUpdate = (Date.now() - parseInt(savedTimestamp)) / (1000 * 60 * 60);
                       if (hoursSinceUpdate < 1) {
                         return 'Updated less than an hour ago';
                       } else if (hoursSinceUpdate < 24) {
                         return `Updated ${hoursSinceUpdate.toFixed(1)} hours ago`;
                       } else {
                         return 'Data may be outdated - consider refreshing';
                       }
                     }
                     return '';
                   })()}
                 </Typography>
               </Box>
             )}

             <Box sx={{ display: 'flex', gap: 2 }}>
               <Button
                 variant="outlined"
                 startIcon={<Psychology />}
                 onClick={() => {
                   loadBusinessInsights();
                 }}
                 sx={{
                   borderRadius: 2,
                   textTransform: 'none',
                   fontWeight: 600,
                   borderColor: 'primary.main',
                   color: 'primary.main',
                   '&:hover': {
                     borderColor: 'primary.dark',
                     backgroundColor: 'rgba(102, 126, 234, 0.04)',
                   }
                 }}
               >
                 Generate New Insights from Updated Data
               </Button>
             </Box>
           </Box>
         </Box>
       </Box>
     </Box>

     {/* Main Content */}
     <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 6 }}>
       <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
         Comprehensive analysis of your business performance, risks, and opportunities
       </Typography>

       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card
           sx={{
             mb: 4,
             borderRadius: 4,
             overflow: 'hidden',
             border: '1px solid rgba(14, 116, 144, 0.22)',
             background: 'linear-gradient(135deg, #0b1f33 0%, #0e3a55 55%, #115e59 100%)',
             color: 'white',
             boxShadow: '0 20px 45px rgba(7, 21, 38, 0.35)'
           }}
         >
           <CardContent sx={{ p: 0 }}>
             <Box sx={{ p: { xs: 3, md: 4 }, background: 'radial-gradient(circle at 85% 0%, rgba(56, 189, 248, 0.25), transparent 45%)' }}>
               <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                 <Box>
                   <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: 'rgba(220, 245, 255, 0.85)' }}>
                     AI Command Center
                   </Typography>
                   <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                     Real-time Assistant for Business Decisions
                   </Typography>
                   <Typography variant="body2" sx={{ color: 'rgba(224, 242, 254, 0.88)', mt: 0.75 }}>
                     Action-first intelligence from your revenue, inventory, and customer behavior data.
                   </Typography>
                 </Box>
                 <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                   <Chip label={`Health ${healthScore}/100`} size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.45)' }} variant="outlined" />
                   <Chip label={`Scope ${keyMetrics.analysisScope || 'ALL_TIME'}`} size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.45)' }} variant="outlined" />
                   <Chip label={`Trend ${trendWindowDays}d`} size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.45)' }} variant="outlined" />
                 </Box>
               </Box>

               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 3 }}>
                 <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: 'rgba(6, 29, 49, 0.58)', border: '1px solid rgba(125, 211, 252, 0.2)' }}>
                   <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <SmartToy sx={{ fontSize: 20 }} />
                     Priority Queue
                   </Typography>
                   {assistantDisplayQueue.length > 0 ? assistantDisplayQueue.map((item) => (
                     <Box key={item.id} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.5, p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)' }}>
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                         {item.icon === 'inventory' && <Inventory sx={{ fontSize: 18, color: '#f59e0b', mt: 0.2 }} />}
                         {item.icon === 'money' && <MonetizationOn sx={{ fontSize: 18, color: '#38bdf8', mt: 0.2 }} />}
                         {item.icon === 'trend' && <TrendingUp sx={{ fontSize: 18, color: '#f97316', mt: 0.2 }} />}
                         {item.icon === 'customer' && <People sx={{ fontSize: 18, color: '#22c55e', mt: 0.2 }} />}
                         <Box>
                           <Typography variant="body2" sx={{ fontWeight: 700, color: '#e0f2fe' }}>{item.title}</Typography>
                           <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.92)' }}>{item.detail}</Typography>
                         </Box>
                       </Box>
                       <Chip
                         label={item.priority}
                         size="small"
                         sx={{
                           height: 20,
                           fontSize: '0.68rem',
                           fontWeight: 700,
                           bgcolor: item.priority === 'HIGH' ? '#b91c1c' : '#b45309',
                           color: 'white'
                         }}
                       />
                     </Box>
                   )) : (
                     <Alert severity="success" sx={{ bgcolor: 'rgba(34, 197, 94, 0.15)', color: '#dcfce7', border: '1px solid rgba(34, 197, 94, 0.35)' }}>
                       No high-priority interventions detected right now.
                     </Alert>
                   )}
                 </Box>

                 <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                   <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(7, 28, 46, 0.6)', border: '1px solid rgba(56, 189, 248, 0.24)' }}>
                     <Typography variant="caption" sx={{ color: 'rgba(186, 230, 253, 0.95)' }}>Net Profit</Typography>
                     <Typography variant="h6" sx={{ fontWeight: 800, color: netProfitValue >= 0 ? '#22c55e' : '#ef4444' }}>
                       {formatCurrency(netProfitValue)}
                     </Typography>
                     <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, marginValue))} sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.18)', '& .MuiLinearProgress-bar': { bgcolor: '#22c55e' } }} />
                     <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.88)' }}>Margin {marginValue.toFixed(1)}%</Typography>
                   </Box>

                   <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(7, 28, 46, 0.6)', border: '1px solid rgba(14, 165, 233, 0.24)' }}>
                     <Typography variant="caption" sx={{ color: 'rgba(186, 230, 253, 0.95)' }}>Collection Ratio</Typography>
                     <Typography variant="h6" sx={{ fontWeight: 800 }}>{(collectionRatioValue * 100).toFixed(1)}%</Typography>
                     <LinearProgress variant="determinate" value={collectionRatioValue * 100} sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.18)', '& .MuiLinearProgress-bar': { bgcolor: '#38bdf8' } }} />
                     <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.88)' }}>Outstanding {outstandingRatioPercent.toFixed(1)}%</Typography>
                   </Box>

                   <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(7, 28, 46, 0.6)', border: '1px solid rgba(34, 197, 94, 0.24)' }}>
                     <Typography variant="caption" sx={{ color: 'rgba(186, 230, 253, 0.95)' }}>Repeat Purchase</Typography>
                     <Typography variant="h6" sx={{ fontWeight: 800 }}>{(repeatRateValue * 100).toFixed(1)}%</Typography>
                     <LinearProgress variant="determinate" value={repeatRateValue * 100} sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.18)', '& .MuiLinearProgress-bar': { bgcolor: '#22c55e' } }} />
                     <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.88)' }}>Top customer: {leadCustomer?.customerName || 'n/a'}</Typography>
                   </Box>

                   <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(7, 28, 46, 0.6)', border: '1px solid rgba(245, 158, 11, 0.24)' }}>
                     <Typography variant="caption" sx={{ color: 'rgba(186, 230, 253, 0.95)' }}>Inventory Signals</Typography>
                     <Typography variant="h6" sx={{ fontWeight: 800 }}>{reorderSuggestionsCount}</Typography>
                     <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block' }}>Reorder suggestions</Typography>
                     <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.88)' }}>
                       Lead SKU: {leadProduct?.productName || 'n/a'}
                     </Typography>
                   </Box>
                 </Box>
               </Box>
             </Box>
           </CardContent>
         </Card>
       )}

       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Box sx={{ mb: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.4fr 1fr' }, gap: 2.5 }}>
           <Card sx={{ borderRadius: 3, border: '1px solid rgba(59, 130, 246, 0.2)', background: 'linear-gradient(135deg, rgba(14,23,39,0.98) 0%, rgba(30,58,95,0.98) 60%, rgba(14,116,144,0.92) 100%)', color: 'white' }}>
             <CardContent sx={{ p: 3 }}>
               <Typography variant="overline" sx={{ letterSpacing: '0.1em', color: 'rgba(186,230,253,0.9)' }}>
                 Assistant Workspace
               </Typography>
               <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                 Today’s Decision Board
               </Typography>
               <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.9)', mb: 2 }}>
                 Prioritized from live sales, inventory, customer, and payment signals.
               </Typography>

               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2.5 }}>
                 {assistantQuickPanels.map((panel) => (
                   <Box key={panel.id} sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)' }}>
                     <Typography variant="caption" sx={{ color: 'rgba(186,230,253,0.9)' }}>{panel.title}</Typography>
                     <Typography variant="h6" sx={{ fontWeight: 800, color: panel.tone }}>{panel.metric}</Typography>
                     <LinearProgress
                       variant="determinate"
                       value={panel.progress}
                       sx={{
                         mt: 0.75,
                         mb: 0.5,
                         bgcolor: 'rgba(255,255,255,0.2)',
                         '& .MuiLinearProgress-bar': { bgcolor: panel.tone }
                       }}
                     />
                     <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.88)' }}>{panel.sub}</Typography>
                   </Box>
                 ))}
               </Box>

               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                 <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)' }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.6, color: '#a5f3fc' }}>Top Revenue Driver</Typography>
                   <Typography variant="body2" sx={{ fontWeight: 700 }}>{leadProduct?.productName || 'No product trend available yet'}</Typography>
                   <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.9)' }}>
                     {leadProduct ? `${leadProduct.totalSold} sold • ${formatCurrency(leadProduct.totalRevenue)} revenue` : `Refresh data to unlock product trend guidance.`}
                   </Typography>
                 </Box>
                 <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)' }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.6, color: '#a5f3fc' }}>Top Relationship Signal</Typography>
                   <Typography variant="body2" sx={{ fontWeight: 700 }}>{leadCustomer?.customerName || 'No customer dominance signal yet'}</Typography>
                   <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.9)' }}>
                     {leadCustomer ? `${formatCurrency(leadCustomer.totalSpent)} spent • ${leadCustomer.purchaseCount} orders` : `Customer pattern appears once transaction depth increases.`}
                   </Typography>
                 </Box>
               </Box>
             </CardContent>
           </Card>

           <Card sx={{ borderRadius: 3, border: '1px solid rgba(14, 116, 144, 0.22)', background: 'linear-gradient(160deg, rgba(255,255,255,0.98) 0%, rgba(236, 253, 245, 0.96) 100%)' }}>
             <CardContent sx={{ p: 3 }}>
               <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                 Opportunity Radar
               </Typography>
               <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                 Best next moves for this cycle
               </Typography>

               <Box sx={{ display: 'grid', gap: 1.2 }}>
                 <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.2)' }}>
                   <Typography variant="body2" sx={{ fontWeight: 700 }}>Demand Signal</Typography>
                   <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                     {demandLead?.title || `No clear demand pattern yet`}
                   </Typography>
                 </Box>
                 <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                   <Typography variant="body2" sx={{ fontWeight: 700 }}>Cross-sell Trigger</Typography>
                   <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                     {crossSellLead ? `${crossSellLead.primaryProduct} -> ${crossSellLead.suggestedProduct}` : `No cross-sell trigger generated yet`}
                   </Typography>
                 </Box>
                 <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
                   <Typography variant="body2" sx={{ fontWeight: 700 }}>Pricing Guidance</Typography>
                   <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                     {pricingLead ? `${pricingLead.productName}: ${pricingLead.action} at ${formatCurrency(pricingLead.suggestedPrice)}` : `Pricing model has no high-confidence move yet`}
                   </Typography>
                 </Box>
               </Box>

               <Divider sx={{ my: 2 }} />
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Typography variant="body2" sx={{ color: 'text.secondary' }}>Active Customers / Day</Typography>
                 <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{activeCustomersPerDayValue}</Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Typography variant="body2" sx={{ color: 'text.secondary' }}>Low Stock Items</Typography>
                 <Typography variant="subtitle1" sx={{ fontWeight: 800, color: lowStockItemsValue > 0 ? 'warning.main' : 'success.main' }}>{lowStockItemsValue}</Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Typography variant="body2" sx={{ color: 'text.secondary' }}>Critical Reorders</Typography>
                 <Typography variant="subtitle1" sx={{ fontWeight: 800, color: criticalReorders > 0 ? 'error.main' : 'success.main' }}>{criticalReorders}</Typography>
               </Box>
             </CardContent>
           </Card>
         </Box>
       )}

       {/* Business Health Overview */}
       <Card sx={{ display: 'none', mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
         <CardContent>
           <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
             <Box display="flex" alignItems="center">
               {getHealthIcon(crossSheetInsights.overallHealth || comprehensiveInsights.businessHealth?.status || 'UNKNOWN')}
               <Typography variant="h5" sx={{ ml: 2, fontWeight: 600 }}>
                 {comprehensiveInsights.businessHealth?.status === 'NOT_LOADED'
                   ? 'Business Health: Data Not Loaded'
                   : `Business Health: ${crossSheetInsights.overallHealth || comprehensiveInsights.businessHealth?.status || 'UNKNOWN'}`
                 }
               </Typography>
             </Box>
             {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
               <Chip
                 label={`Score: ${crossSheetInsights.keyMetrics?.businessHealthScore || comprehensiveInsights.businessHealth?.score || 0}/100`}
                 color={getHealthColor(crossSheetInsights.overallHealth || comprehensiveInsights.businessHealth?.status || 'UNKNOWN')}
                 sx={{ color: 'white', borderColor: 'white' }}
                 variant="outlined"
               />
             )}
           </Box>

           {comprehensiveInsights.businessHealth?.status === 'NOT_LOADED' ? (
             <Box sx={{ textAlign: 'center', py: 3 }}>
               <Typography variant="body1" sx={{ mb: 2 }}>
                 📊 Click "Load Data" to analyze your business health
               </Typography>
               <Button
                 variant="contained"
                 startIcon={<Refresh />}
                 onClick={() => {
                   loadBusinessInsights();
                 }}
                 sx={{
                   backgroundColor: 'white',
                   color: 'primary.main',
                   '&:hover': {
                     backgroundColor: 'rgba(255,255,255,0.9)',
                   }
                 }}
               >
                 Refresh Data
               </Button>
             </Box>
           ) : (
             <>
               <LinearProgress
                 variant="determinate"
                 value={crossSheetInsights.keyMetrics?.businessHealthScore || comprehensiveInsights.businessHealth?.score || 0}
                 sx={{
                   height: 8,
                   borderRadius: 4,
                   mb: 2,
                   backgroundColor: 'rgba(255,255,255,0.3)',
                   '& .MuiLinearProgress-bar': {
                     backgroundColor: 'white',
                   }
                 }}
               />

               {comprehensiveInsights.businessHealth?.alerts && comprehensiveInsights.businessHealth.alerts.length > 0 && (
                 <Box mt={2}>
                   <Typography variant="h6" gutterBottom>Health Alerts:</Typography>
                   {comprehensiveInsights.businessHealth.alerts.map((alert, index) => (
                     <Alert key={index} severity="warning" sx={{ mb: 1, color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }}>
                       {alert}
                     </Alert>
                   ))}
                 </Box>
               )}
             </>
           )}
         </CardContent>
       </Card>

       {/* Executive Summary - What This Means for Your Business */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ display: 'none', mb: 6, bgcolor: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <Assessment sx={{ fontSize: 24 }} />
               Executive Summary - What This Means for Your Business
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4 }}>
               <Box>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                   ✅ Your Business Strengths:
                 </Typography>
                 <Box sx={{ pl: 2 }}>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                     Strong revenue generation - Your sales are performing well
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                     Good profit margins - You're keeping costs under control
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                     Customer loyalty - Your customers keep coming back
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                     Inventory management - Stock levels are optimized
                   </Typography>
                 </Box>
               </Box>

               <Box>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                   🎯 Areas for Improvement:
                 </Typography>
                 <Box sx={{ pl: 2 }}>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                     Customer acquisition - Focus on getting new customers
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                     Operational efficiency - Streamline your processes
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                     Market expansion - Explore new sales channels
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Warning sx={{ fontSize: 16, color: 'warning.main' }} />
                     Technology adoption - Use modern tools for efficiency
                   </Typography>
                 </Box>
               </Box>
             </Box>

             <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                 💡 Simple Action Plan:
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Week 1:</strong> Focus on your top-selling products and best customers
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Week 2:</strong> Address any inventory issues and stock up on popular items
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Week 3:</strong> Implement one process improvement suggested below
               </Typography>
               <Typography variant="body2">
                 <strong>Week 4:</strong> Review your progress and plan next month's improvements
               </Typography>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Financial Performance Analysis */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ display: 'none', mb: 6 }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <MonetizationOn sx={{ fontSize: 24 }} />
               Financial Performance Analysis
             </Typography>

             <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
               💡 <strong>Understanding Your Money:</strong> This section shows how much money your business is making, spending, and keeping as profit. These numbers help you understand if your business is financially healthy.
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
               <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                 <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main', mb: 1 }}>
                   ₹{(() => {
                     // Get total revenue from real data
                     const totalRevenue = crossSheetInsights.keyMetrics?.totalRevenue ||
                                        comprehensiveInsights.keyPerformanceIndicators?.salesKPIs?.totalRevenue ||
                                        0;
                     console.log('📊 FRONTEND: Total Revenue from real data:', totalRevenue);
                     return totalRevenue.toLocaleString('en-IN');
                   })()}
                 </Typography>
                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                   Total Revenue
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'success.dark' }}>
                   💰 Money coming into your business from sales
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 3, border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                 <Typography variant="h4" sx={{ fontWeight: 800, color: 'error.main', mb: 1 }}>
                   ₹{(() => {
                     // Total costs = COGS + operating expenses
                     const totalExpenses = crossSheetInsights.keyMetrics?.totalExpenses ||
                                         comprehensiveInsights.keyPerformanceIndicators?.financialKPIs?.totalExpenses ||
                                         0;
                     console.log('📊 FRONTEND: Total Expenses from real data:', totalExpenses);
                     return totalExpenses.toLocaleString('en-IN');
                   })()}
                 </Typography>
                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                   Total Costs (COGS + Opex)
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'error.dark' }}>
                   Cost of sold goods plus operating expenses
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                   COGS ₹{(crossSheetInsights.keyMetrics?.totalCogs || 0).toLocaleString('en-IN')} • Opex ₹{(crossSheetInsights.keyMetrics?.totalOperatingExpenses || 0).toLocaleString('en-IN')}
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                 <Typography variant="h4" sx={{
                   fontWeight: 800,
                   color: (() => {
                     const netProfit = crossSheetInsights.keyMetrics?.netProfit ||
                                      comprehensiveInsights.keyPerformanceIndicators?.financialKPIs?.netProfit ||
                                      0;
                     return netProfit >= 0 ? 'success.main' : 'error.main';
                   })(),
                   mb: 1
                 }}>
                   ₹{(() => {
                     // Net Profit = Revenue - COGS - Operating Expenses
                     const netProfit = crossSheetInsights.keyMetrics?.netProfit ||
                                      comprehensiveInsights.keyPerformanceIndicators?.financialKPIs?.netProfit ||
                                      0;
                     console.log('📊 FRONTEND: Net Profit from real data:', netProfit);
                     return netProfit.toLocaleString('en-IN');
                   })()}
                 </Typography>
                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                   Net Profit
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'info.dark' }}>
                   💎 Money your business keeps after all expenses
                 </Typography>
               </Box>
             </Box>

             <Box sx={{ p: 3, bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 3, border: '1px solid rgba(255, 193, 7, 0.2)' }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                 📊 What These Numbers Mean:
               </Typography>
               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                 <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {(() => {
                        const netProfit = crossSheetInsights.keyMetrics?.netProfit ||
                                          comprehensiveInsights.keyPerformanceIndicators?.financialKPIs?.netProfit ||
                                          0;
                        return netProfit >= 0
                          ? <><strong>Profit Status:</strong> Your current period is profitable.</>
                          : <><strong>Profit Status:</strong> Current period is in loss. Margin and cost corrections are needed.</>;
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Revenue vs Costs:</strong> For every ₹100 of cost, you're generating ₹{(() => {
                        const totalRevenue = crossSheetInsights.keyMetrics?.totalRevenue ||
                                           comprehensiveInsights.keyPerformanceIndicators?.salesKPIs?.totalRevenue ||
                                           0;
                        const totalExpenses = crossSheetInsights.keyMetrics?.totalExpenses ||
                                            comprehensiveInsights.keyPerformanceIndicators?.financialKPIs?.totalExpenses ||
                                            0;

                        return totalExpenses > 0 ? ((totalRevenue / totalExpenses) * 100).toFixed(0) : 0;
                      })()} in revenue.
                    </Typography>
                 </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {(() => {
                        const netProfit = crossSheetInsights.keyMetrics?.netProfit ||
                                          comprehensiveInsights.keyPerformanceIndicators?.financialKPIs?.netProfit ||
                                          0;
                        return netProfit >= 0
                          ? <><strong>Action Items:</strong> Protect margins on top products and keep replenishment timely.</>
                          : <><strong>Action Items:</strong> Cut low-return spend, improve pricing on fast movers, and optimize dealer costs.</>;
                      })()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Growth Potential:</strong> Use demand patterns, seasonality, and reorder predictions to capture peak sales windows.
                    </Typography>
                  </Box>
               </Box>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Customer Analysis */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (businessInsights.personalizedInsights?.topCustomers?.length > 0 || insights?.crossSheetInsights?.personalizedInsights?.topCustomers?.length > 0) && (
         <Card sx={{ display: 'none', mb: 6 }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <People sx={{ fontSize: 24 }} />
               Customer Analysis - Who Buys From You
             </Typography>

             <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
               💡 <strong>Understanding Your Customers:</strong> These are the people who buy from you most often and spend the most money. They're your most valuable customers - treat them well!
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
               {(() => {
                 // Get customers from real data - no hardcoded values
                 const customers = businessInsights.personalizedInsights?.topCustomers || insights?.crossSheetInsights?.personalizedInsights?.topCustomers || [];
                 console.log('📊 Customer Analysis: Real customer data count:', customers.length);
                 console.log('📊 Customer Analysis: Customer data:', customers);

                 // Show all customers from real data, no hardcoded limit
                 return customers.map((customer, index) => (
                   <Box key={index} sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                     <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main', mb: 2 }}>
                       👑 {customer.customerName || 'Unknown Customer'}
                     </Typography>
                     <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total Spent</Typography>
                        <Typography variant="h6" sx={{ color: 'success.main' }}>{formatCurrency(customer.totalSpent || 0)}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Orders</Typography>
                         <Typography variant="h6" sx={{ color: 'success.main' }}>{customer.purchaseCount || 0}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Avg Order</Typography>
                        <Typography variant="h6" sx={{ color: 'success.main' }}>{formatCurrency(customer.avgOrderValue || 0)}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loyalty</Typography>
                         <Chip
                           label={customer.loyalty || 'NEW'}
                           size="small"
                           sx={{
                             bgcolor: (customer.loyalty || 'NEW') === 'HIGH' ? 'success.main' : (customer.loyalty || 'NEW') === 'MEDIUM' ? 'warning.main' : 'info.main',
                             color: 'white'
                           }}
                         />
                       </Box>
                     </Box>
                   </Box>
                 ));
               })()}
             </Box>

             <Box sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'info.main' }}>
                 🎯 What This Means for Your Business:
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>🏆 VIP Customers:</strong> These customers are your business champions! They buy frequently and spend more than average.
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>💝 Customer Loyalty:</strong> High loyalty means customers trust your products and service quality.
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>📈 Growth Strategy:</strong> Focus on keeping these customers happy and encourage them to buy more often.
               </Typography>
               <Typography variant="body2">
                 <strong>🎁 Reward Program:</strong> Consider offering special discounts or early access to new products for your top customers.
               </Typography>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Inventory Management */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && businessInsights.personalizedInsights?.lowStockAlerts?.length > 0 && (
         <Card sx={{ display: 'none', mb: 6 }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <Inventory sx={{ fontSize: 24 }} />
               Inventory Management - Stock Control
             </Typography>

             <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
               💡 <strong>Understanding Inventory:</strong> This shows which products are running low on stock. Running out of popular items means lost sales - this helps you know what to order more of.
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
               {businessInsights.personalizedInsights?.lowStockAlerts?.slice(0, 6).map((item, index) => (
                 <Box key={index} sx={{ p: 3, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 3, border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                   <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main', mb: 2 }}>
                     ⚠️ {item.productName || item.itemName || 'Unknown Product'}
                   </Typography>
                   <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                     <Box>
                       <Typography variant="body2" sx={{ color: 'text.secondary' }}>Current Stock</Typography>
                       <Typography variant="h6" sx={{ color: 'error.main' }}>{item.currentStock || item.quantity || 0} units</Typography>
                     </Box>
                     <Box>
                       <Typography variant="body2" sx={{ color: 'text.secondary' }}>Status</Typography>
                       <Chip
                         label="LOW STOCK"
                         size="small"
                         color="error"
                       />
                     </Box>
                   </Box>
                   <Typography variant="caption" sx={{ color: 'error.dark', mt: 2, display: 'block' }}>
                     🛒 This item needs to be reordered soon to avoid stockouts
                   </Typography>
                 </Box>
               ))}
             </Box>

             <Box sx={{ p: 3, bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 3, border: '1px solid rgba(255, 193, 7, 0.2)' }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                 📦 What This Means:
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>🚨 Action Required:</strong> These items are running low and might sell out soon. Stocking out means losing customers to competitors.
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>💰 Impact:</strong> Each stockout could cost you ₹500-₹5000 in lost sales, depending on the item.
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>📊 Priority:</strong> Focus on reordering your most popular items first (check sales data).
               </Typography>
               <Typography variant="body2">
                 <strong>🎯 Solution:</strong> Set up automatic reordering when stock falls below 10 units for popular items.
               </Typography>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Critical Alerts & Action Items */}
       {comprehensiveInsights.businessHealth?.status === 'NOT_LOADED' ? (
         <Box sx={{ textAlign: 'center', py: 4 }}>
           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
             📊 Ready to Load Business Insights
           </Typography>
           <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
             Click the "Load Data" button to fetch and analyze your business data for insights, alerts, and recommendations.
           </Typography>
         </Box>
       ) : (
         <Box sx={{
           display: 'none',
           gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
           gap: 3,
           mb: 6,
         }}>
           {/* Low Stock Alert */}
           <Card sx={{
             border: '1px solid',
             borderColor: (businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0 ? 'warning.main' : 'success.main',
             background: (businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0 ? 'warning.light' : 'success.light',
             opacity: 0.9,
           }}>
             <CardContent sx={{ p: 3 }}>
               <Typography variant="h6" sx={{
                 fontWeight: 600,
                 color: (businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0 ? 'warning.dark' : 'success.dark',
                 mb: 2,
                 display: 'flex',
                 alignItems: 'center',
                 gap: 1
               }}>
                 {(businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0 ? <Warning color="warning" /> : <CheckCircle color="success" />}
                 {(businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0 ? 'Low Stock Alert' : 'Stock Levels Good'}
               </Typography>
               <Typography variant="body2" sx={{
                 color: (businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0 ? 'warning.dark' : 'success.dark'
               }}>
                 {(businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0
                   ? `${businessInsights.personalizedInsights.lowStockAlerts.length} items need restocking`
                   : 'All items sufficiently stocked'
                 }
               </Typography>
             </CardContent>
           </Card>

           {/* Business Alerts */}
           <Card sx={{
             border: '1px solid',
             borderColor: (crossSheetInsights.alerts?.length || 0) > 0 ? 'error.main' : 'success.main',
             background: (crossSheetInsights.alerts?.length || 0) > 0 ? 'error.light' : 'success.light',
             opacity: 0.9,
           }}>
             <CardContent sx={{ p: 3 }}>
               <Typography variant="h6" sx={{
                 fontWeight: 600,
                 color: (crossSheetInsights.alerts?.length || 0) > 0 ? 'error.dark' : 'success.dark',
                 mb: 2,
                 display: 'flex',
                 alignItems: 'center',
                 gap: 1
               }}>
                 {(crossSheetInsights.alerts?.length || 0) > 0 ? <Warning color="error" /> : <CheckCircle color="success" />}
                 {(crossSheetInsights.alerts?.length || 0) > 0 ? 'Business Alerts' : 'Business Status'}
               </Typography>
               <Typography variant="body2" sx={{
                 color: (crossSheetInsights.alerts?.length || 0) > 0 ? 'error.dark' : 'success.dark'
               }}>
                 {(crossSheetInsights.alerts?.length || 0) > 0
                   ? `${crossSheetInsights.alerts.length} alerts require attention`
                   : 'All systems operating normally'
                 }
               </Typography>
             </CardContent>
           </Card>

           {/* Reorder Suggestions */}
           <Card sx={{
             border: '1px solid',
             borderColor: (businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length || 0) > 0 ? 'info.main' : 'grey.400',
             background: (businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length || 0) > 0 ? 'info.light' : 'grey.100',
             opacity: 0.9,
           }}>
             <CardContent sx={{ p: 3 }}>
               <Typography variant="h6" sx={{
                 fontWeight: 600,
                 color: (businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length || 0) > 0 ? 'info.dark' : 'text.secondary',
                 mb: 2,
                 display: 'flex',
                 alignItems: 'center',
                 gap: 1
               }}>
                 <Inventory color={(businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length || 0) > 0 ? 'info' : 'inherit'} />
                 Reorder Suggestions
               </Typography>
               <Typography variant="body2" sx={{
                 color: (businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length || 0) > 0 ? 'info.dark' : 'text.secondary'
               }}>
                 {(businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length || 0) > 0
                   ? `${businessInsights.personalizedInsights.proactiveReorderSuggestions.length} items suggested for reorder`
                   : 'No reorder suggestions at this time'
                 }
               </Typography>
             </CardContent>
           </Card>
         </Box>
       )}

       {/* Operational Efficiency */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ display: 'none', mb: 6 }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <Timeline sx={{ fontSize: 24 }} />
               Operational Efficiency - How Well Your Business Runs
             </Typography>

             <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
               💡 <strong>Understanding Operations:</strong> This section shows how smoothly your business processes are working. Good operations mean happy customers and lower costs.
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
               <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                 <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main', mb: 1 }}>
                   {crossSheetInsights.keyMetrics?.avgCustomersPerDay ||
                    (crossSheetInsights.keyMetrics?.activeCustomers
                      ? Number((crossSheetInsights.keyMetrics.activeCustomers / 30).toFixed(2))
                      : 0)}
                 </Typography>
                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                   Active Customers/Day
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'success.dark' }}>
                   Average customers visiting per day
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                 <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main', mb: 1 }}>
                   {crossSheetInsights.keyMetrics?.totalInventoryCount ||
                    crossSheetInsights.keyMetrics?.totalInventory ||
                    0}
                 </Typography>
                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                   Products in Stock
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'info.dark' }}>
                   Items available for sale
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 3, border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                 <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.main', mb: 1 }}>
                   {crossSheetInsights.keyMetrics?.lowStockItems ||
                    businessInsights.personalizedInsights?.lowStockAlerts?.length ||
                    businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length ||
                    0}
                 </Typography>
                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                   Items Need Restock
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'warning.dark' }}>
                   Running low on inventory
                 </Typography>
               </Box>
             </Box>

             <Box sx={{ p: 3, bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 3, border: '1px solid rgba(156, 39, 176, 0.2)' }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                 📊 What This Means for Your Operations:
               </Typography>
               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                 <Box>
                   <Typography variant="body2" sx={{ mb: 2 }}>
                     <strong>✅ Good Performance:</strong> Your customer base is growing and you're managing inventory well.
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 2 }}>
                     <strong>⚡ Efficiency:</strong> You have the right amount of stock for your sales volume.
                   </Typography>
                 </Box>
                 <Box>
                   <Typography variant="body2" sx={{ mb: 2 }}>
                     <strong>🎯 Optimization:</strong> Focus on your best-selling products and most valuable customers.
                   </Typography>
                   <Typography variant="body2">
                     <strong>📈 Growth:</strong> Consider expanding your product range or customer reach.
                   </Typography>
                 </Box>
               </Box>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Growth Opportunities */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ display: 'none', mb: 6 }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <Lightbulb sx={{ fontSize: 24 }} />
               Growth Opportunities - How to Expand Your Business
             </Typography>

             <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
               💡 <strong>Understanding Growth:</strong> These are specific ways your business can make more money and serve more customers. Each opportunity includes simple steps to implement.
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
               <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main', mb: 2 }}>
                   📈 Increase Sales Volume
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 2 }}>
                   Your current customers are loyal - encourage them to buy more often.
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'success.dark' }}>
                   <strong>Action:</strong> Create combo offers and loyalty discounts
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 2 }}>
                   👥 Get New Customers
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 2 }}>
                   Expand your reach to find customers similar to your best ones.
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'info.dark' }}>
                   <strong>Action:</strong> Ask happy customers for referrals
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 3, border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main', mb: 2 }}>
                   🛒 Add New Products
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 2 }}>
                   Your top-selling items show what customers want - add similar products.
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'warning.dark' }}>
                   <strong>Action:</strong> Research complementary products
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(156, 39, 176, 0.1)', borderRadius: 3, border: '1px solid rgba(156, 39, 176, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main', mb: 2 }}>
                   💰 Improve Pricing
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 2 }}>
                   Your profit margins are good - consider strategic price increases.
                 </Typography>
                 <Typography variant="caption" sx={{ color: 'secondary.dark' }}>
                   <strong>Action:</strong> Test small price increases on top items
                 </Typography>
               </Box>
             </Box>

             <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                 🎯 Simple Growth Plan:
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Week 1:</strong> Contact your top 5 customers and ask what other products they need
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Week 2:</strong> Create special offers for your most popular products
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Week 3:</strong> Ask 3 satisfied customers for referrals or testimonials
               </Typography>
               <Typography variant="body2">
                 <strong>Week 4:</strong> Research one new product line that complements your best-sellers
               </Typography>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Risk Assessment */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ display: 'none', mb: 6 }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <Security sx={{ fontSize: 24 }} />
               Risk Assessment - What Could Go Wrong
             </Typography>

             <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
               💡 <strong>Understanding Risks:</strong> Every business has some risks. This section helps you identify potential problems before they happen, so you can prevent them.
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
               <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main', mb: 2 }}>
                   ✅ Low Risk Areas
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 1 }}>
                   • Customer satisfaction is high
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 1 }}>
                   • Profit margins are healthy
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 1 }}>
                   • Inventory levels are managed well
                 </Typography>
                 <Typography variant="body2">
                   • Business processes are efficient
                 </Typography>
               </Box>

               <Box sx={{ p: 3, bgcolor: 'rgba(255, 193, 7, 0.1)', borderRadius: 3, border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main', mb: 2 }}>
                   ⚠️ Monitor These Areas
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 1 }}>
                   • Stock levels of popular items
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 1 }}>
                   • Customer payment collections
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 1 }}>
                   • Supplier reliability
                 </Typography>
                 <Typography variant="body2">
                   • Market competition changes
                 </Typography>
               </Box>
             </Box>

             <Box sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'info.main' }}>
                 🛡️ Risk Prevention Plan:
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Daily:</strong> Check inventory levels and reorder when items fall below 10 units
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Weekly:</strong> Follow up on customer payments that are overdue
               </Typography>
               <Typography variant="body2" sx={{ mb: 2 }}>
                 <strong>Monthly:</strong> Review your top-selling products and customer feedback
               </Typography>
               <Typography variant="body2">
                 <strong>Quarterly:</strong> Research competitors and market trends in your industry
               </Typography>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Detailed Action Items */}
       {((businessInsights.personalizedInsights?.lowStockAlerts?.length || 0) > 0 || (businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length || 0) > 0) && (
         <Card sx={{ display: 'none', mb: 6, bgcolor: 'grey.50' }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
               <Assessment sx={{ fontSize: 20 }} />
               Action Items
             </Typography>

             {businessInsights.personalizedInsights?.lowStockAlerts?.length > 0 && (
               <Box sx={{ mb: 3 }}>
                 <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'warning.dark', mb: 2 }}>
                   ⚠️ Low Stock Items:
                 </Typography>
                 <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                   {businessInsights.personalizedInsights.lowStockAlerts.slice(0, 6).map((item, index) => (
                     <Box key={index} sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2, border: '1px solid rgba(255, 152, 0, 0.2)' }}>
                       <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                         {item.productName || item.itemName || 'Unknown Product'}
                       </Typography>
                       <Typography variant="caption" sx={{ color: 'warning.dark' }}>
                         {item.currentStock || item.quantity || 0} units remaining
                       </Typography>
                     </Box>
                   ))}
                 </Box>
                 {businessInsights.personalizedInsights.lowStockAlerts.length > 6 && (
                   <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                     ...and {businessInsights.personalizedInsights.lowStockAlerts.length - 6} more items
                   </Typography>
                 )}
               </Box>
             )}

             {businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length > 0 && (
               <Box>
                 <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'info.dark', mb: 2 }}>
                   📦 Reorder Suggestions:
                 </Typography>
                 <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                   {businessInsights.personalizedInsights.proactiveReorderSuggestions.slice(0, 6).map((suggestion, index) => (
                     <Box key={index} sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                       <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.dark' }}>
                         {suggestion.productName || suggestion.itemName || 'Unknown Product'}
                       </Typography>
                       <Typography variant="caption" sx={{ color: 'info.dark', display: 'block' }}>
                         Order {suggestion.suggestedReorder || suggestion.reorderQuantity || 0} units
                       </Typography>
                       <Chip
                         label={suggestion.priority || 'MEDIUM'}
                         size="small"
                         sx={{
                           mt: 1,
                           fontSize: '0.7rem',
                           height: 18,
                           bgcolor: (suggestion.priority || 'MEDIUM') === 'CRITICAL' ? 'error.main' :
                                    (suggestion.priority || 'MEDIUM') === 'HIGH' ? 'warning.main' :
                                    (suggestion.priority || 'MEDIUM') === 'MEDIUM' ? 'info.main' : 'success.main',
                           color: 'white'
                         }}
                       />
                     </Box>
                   ))}
                 </Box>
                 {businessInsights.personalizedInsights.proactiveReorderSuggestions.length > 6 && (
                   <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                     ...and {businessInsights.personalizedInsights.proactiveReorderSuggestions.length - 6} more suggestions
                   </Typography>
                 )}
               </Box>
             )}
           </CardContent>
         </Card>
       )}

       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ mb: 6, borderRadius: 3, border: '1px solid rgba(14, 116, 144, 0.2)', bgcolor: 'background.paper' }}>
           <CardContent sx={{ p: 3 }}>
             <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
               <Assessment sx={{ fontSize: 20 }} />
               Action Board
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.45fr 1fr' }, gap: 2 }}>
               <Box sx={{ border: '1px solid rgba(148,163,184,0.26)', borderRadius: 2.5, overflow: 'hidden' }}>
                 <Box sx={{ px: 2, py: 1.2, bgcolor: 'rgba(14,165,233,0.08)', borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Reorder Queue</Typography>
                 </Box>
                 {(reorderSuggestionsList.slice(0, 8)).map((suggestion, index) => (
                   <Box
                     key={index}
                     sx={{
                       px: 2,
                       py: 1.35,
                       borderBottom: index < Math.min(7, reorderSuggestionsList.length - 1) ? '1px solid rgba(148,163,184,0.16)' : 'none',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'space-between',
                       gap: 1
                     }}
                   >
                     <Box>
                       <Typography variant="body2" sx={{ fontWeight: 700 }}>
                         {suggestion.productName || suggestion.itemName || 'Unknown Product'}
                       </Typography>
                       <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                         Stock {suggestion.currentStock || 0} • Daily {suggestion.dailySales || 0} • Reorder {suggestion.suggestedReorder || suggestion.reorderQuantity || 0}
                       </Typography>
                     </Box>
                     <Chip
                       label={suggestion.priority || 'MEDIUM'}
                       size="small"
                       sx={{
                         fontWeight: 700,
                         bgcolor: (suggestion.priority || 'MEDIUM') === 'CRITICAL' ? 'error.main' :
                                  (suggestion.priority || 'MEDIUM') === 'HIGH' ? 'warning.main' :
                                  (suggestion.priority || 'MEDIUM') === 'MEDIUM' ? 'info.main' : 'success.main',
                         color: 'white'
                       }}
                     />
                   </Box>
                 ))}
                 {reorderSuggestionsList.length === 0 && (
                   <Box sx={{ px: 2, py: 2 }}>
                     <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                       No immediate reorder actions.
                     </Typography>
                   </Box>
                 )}
               </Box>

               <Box sx={{ display: 'grid', gap: 1.25 }}>
                 <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.6 }}>Urgency Signals</Typography>
                   <Typography variant="body2">Critical reorders: <strong>{criticalReorders}</strong></Typography>
                   <Typography variant="body2">Low stock alerts: <strong>{lowStockAlertList.length}</strong></Typography>
                 </Box>
                 <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.6 }}>Fastest Revenue Move</Typography>
                   <Typography variant="body2">
                     {crossSellLead
                       ? `Promote ${crossSellLead.suggestedProduct} with ${crossSellLead.primaryProduct}`
                       : `Push top-selling SKU follow-ups`}
                   </Typography>
                 </Box>
                 <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.2)' }}>
                   <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.6 }}>Pricing Signal</Typography>
                   <Typography variant="body2">
                     {pricingLead
                       ? `${pricingLead.productName}: ${pricingLead.action} at ${formatCurrency(pricingLead.suggestedPrice)}`
                       : `No high-confidence price action right now`}
                   </Typography>
                 </Box>
               </Box>
             </Box>
           </CardContent>
         </Card>
       )}

       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ mb: 6, borderRadius: 3, border: '1px solid rgba(99, 102, 241, 0.18)', bgcolor: 'background.paper' }}>
           <CardContent sx={{ p: 3 }}>
             <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
               <Psychology sx={{ fontSize: 20 }} />
               AI Insight Stream
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, 1fr)' }, gap: 2 }}>
               <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid rgba(148,163,184,0.2)', bgcolor: 'rgba(22,163,74,0.05)' }}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>Top Products</Typography>
                 {(topProductsList.slice(0, 4)).map((product, idx) => (
                   <Box key={idx} sx={{ py: 0.9, borderBottom: idx < Math.min(3, topProductsList.length - 1) ? '1px dashed rgba(148,163,184,0.35)' : 'none' }}>
                     <Typography variant="body2" sx={{ fontWeight: 700 }}>{product.productName}</Typography>
                     <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                       {product.totalSold || 0} sold • {formatCurrency(product.totalRevenue || 0)} • {product.dailySales || 0}/day
                     </Typography>
                   </Box>
                 ))}
               </Box>

               <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid rgba(148,163,184,0.2)', bgcolor: 'rgba(59,130,246,0.05)' }}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>Top Customers</Typography>
                 {(topCustomersList.slice(0, 4)).map((customer, idx) => (
                   <Box key={idx} sx={{ py: 0.9, borderBottom: idx < Math.min(3, topCustomersList.length - 1) ? '1px dashed rgba(148,163,184,0.35)' : 'none' }}>
                     <Typography variant="body2" sx={{ fontWeight: 700 }}>{customer.customerName}</Typography>
                     <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                       {formatCurrency(customer.totalSpent || 0)} • {customer.purchaseCount || 0} orders • {formatCurrency(customer.avgOrderValue || 0)}/order
                     </Typography>
                   </Box>
                 ))}
               </Box>

               <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid rgba(148,163,184,0.2)', bgcolor: 'rgba(245,158,11,0.05)' }}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>Demand & Timing</Typography>
                 {(demandPatternsList.slice(0, 2)).map((pattern, idx) => (
                   <Box key={idx} sx={{ py: 0.9, borderBottom: idx < Math.min(1, demandPatternsList.length - 1) ? '1px dashed rgba(148,163,184,0.35)' : 'none' }}>
                     <Typography variant="body2" sx={{ fontWeight: 700 }}>{pattern.title}</Typography>
                     <Typography variant="caption" sx={{ color: 'text.secondary' }}>{pattern.recommendation}</Typography>
                   </Box>
                 ))}
                 {demandPatternsList.length === 0 && (
                   <Typography variant="caption" sx={{ color: 'text.secondary' }}>No timing patterns available yet.</Typography>
                 )}
               </Box>

               <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid rgba(148,163,184,0.2)', bgcolor: 'rgba(168,85,247,0.05)' }}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>Commercial Plays</Typography>
                 <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.4 }}>Pricing</Typography>
                 <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.2 }}>
                   {priceOptimizationList[0]
                     ? `${priceOptimizationList[0].productName}: ${priceOptimizationList[0].action} at ${formatCurrency(priceOptimizationList[0].suggestedPrice)}`
                     : 'No pricing move generated.'}
                 </Typography>
                 <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.4 }}>Cross-sell</Typography>
                 <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                   {crossSellOpportunitiesList[0]
                     ? `${crossSellOpportunitiesList[0].primaryProduct} -> ${crossSellOpportunitiesList[0].suggestedProduct}`
                     : 'No cross-sell pair detected.'}
                 </Typography>
               </Box>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* AI-Powered Business Insights */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ display: 'none', mb: 6, bgcolor: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
               <Psychology sx={{ fontSize: 24 }} />
               AI-Powered Business Insights - Real Data Analysis
             </Typography>

             <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
               💡 <strong>Real AI Analysis:</strong> These insights are generated from your actual sales data, showing specific product names, pricing recommendations, and actionable business strategies.
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4 }}>
               {/* Trending Products */}
               {businessInsights.personalizedInsights?.trendingProducts?.length > 0 && (
                 <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                   <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                     <TrendingUp sx={{ fontSize: 20 }} />
                     Trending Products - Your Best Sellers
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                     These products are selling well. Focus on keeping them in stock and consider promoting them more.
                   </Typography>
                   {businessInsights.personalizedInsights.trendingProducts.slice(0, 4).map((product, index) => (
                     <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                       <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                         {product.productName}
                       </Typography>
                       <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                         {product.totalSold} sold • ₹{product.totalRevenue} revenue • {product.dailySales}/day
                       </Typography>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Chip
                           label={product.trend}
                           size="small"
                           sx={{
                             fontSize: '0.7rem',
                             height: 20,
                             bgcolor: product.trend === 'HIGH' ? 'success.main' : product.trend === 'MEDIUM' ? 'warning.main' : 'info.main',
                             color: 'white'
                           }}
                         />
                         <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                           {product.uniqueCustomers} customers
                         </Typography>
                       </Box>
                     </Box>
                   ))}
                 </Box>
               )}

               {/* Top Customers */}
               {businessInsights.personalizedInsights?.topCustomers?.length > 0 && (
                 <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                   <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                     <People sx={{ fontSize: 20 }} />
                     Top Customers - Your VIP Buyers
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                     These customers love your products! They're loyal and spend more than average. Keep them happy!
                   </Typography>
                   {businessInsights.personalizedInsights.topCustomers.slice(0, 4).map((customer, index) => (
                     <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                       <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                         {customer.customerName}
                       </Typography>
                       <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {formatCurrency(customer.totalSpent)} spent • {customer.purchaseCount} orders • {formatCurrency(customer.avgOrderValue)}/order
                       </Typography>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Chip
                           label={customer.loyalty}
                           size="small"
                           sx={{
                             fontSize: '0.7rem',
                             height: 20,
                             bgcolor: customer.loyalty === 'HIGH' ? 'success.main' : customer.loyalty === 'MEDIUM' ? 'warning.main' : 'info.main',
                             color: 'white'
                           }}
                         />
                         <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                           {customer.purchaseFrequency}/day
                         </Typography>
                       </Box>
                     </Box>
                   ))}
                 </Box>
               )}

               {/* Demand Patterns */}
               {businessInsights.personalizedInsights?.demandPatterns?.length > 0 && (
                 <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                   <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Timeline sx={{ fontSize: 20 }} />
                     Demand Patterns - When People Buy
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                     Understanding when customers buy helps you plan staffing, inventory, and marketing timing.
                   </Typography>
                   {businessInsights.personalizedInsights.demandPatterns.slice(0, 3).map((pattern, index) => (
                     <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                       <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                         {pattern.title}
                       </Typography>
                       <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                         {pattern.insight}
                       </Typography>
                       <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                         <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 500 }}>
                           💡 {pattern.recommendation}
                         </Typography>
                       </Box>
                     </Box>
                   ))}
                 </Box>
               )}

               {/* Smart Reorders */}
               {businessInsights.personalizedInsights?.proactiveReorderSuggestions?.length > 0 && (
                 <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                   <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'info.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                     <AutoGraph sx={{ fontSize: 20 }} />
                     Smart Reorder Predictions - AI Stock Management
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                     The AI predicts when you'll run out of stock based on your sales patterns. This prevents lost sales from stockouts.
                   </Typography>
                   {businessInsights.personalizedInsights.proactiveReorderSuggestions.slice(0, 4).map((suggestion, index) => (
                     <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                       <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                         {suggestion.productName}
                       </Typography>
                       <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                         {suggestion.currentStock} in stock • {suggestion.dailySales}/day • {suggestion.daysOfStock} days left
                       </Typography>
                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                         <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                           Suggested: Order {suggestion.suggestedReorder} units
                         </Typography>
                         <Chip
                           label={suggestion.priority}
                           size="small"
                           sx={{
                             fontSize: '0.7rem',
                             height: 20,
                             bgcolor: suggestion.priority === 'CRITICAL' ? 'error.main' :
                                      suggestion.priority === 'HIGH' ? 'warning.main' :
                                      suggestion.priority === 'MEDIUM' ? 'info.main' : 'success.main',
                             color: 'white'
                           }}
                         />
                       </Box>
                     </Box>
                   ))}
                 </Box>
               )}
             </Box>

             {/* Product Combination Analysis */}
             {businessInsights.personalizedInsights.productCombinations?.combinations?.length > 0 && (
               <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(255, 193, 7, 0.05)', borderRadius: 3, border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                   <AutoGraph sx={{ fontSize: 20 }} />
                   Product Combination Analysis - What Customers Buy Together
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                   AI analysis of which products are frequently purchased together. Use this to create bundle offers and improve cross-selling.
                 </Typography>

                 {businessInsights.personalizedInsights.productCombinations.combinations.slice(0, 3).map((combo, index) => (
                   <Box key={index} sx={{ mb: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                     <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main', mb: 2 }}>
                       🛒 Popular Bundle: {combo.products.join(' + ')}
                     </Typography>
                     <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Times Bought Together</Typography>
                         <Typography variant="h6" sx={{ color: 'warning.main' }}>{combo.frequency}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Average Order Value</Typography>
                         <Typography variant="h6" sx={{ color: 'warning.main' }}>₹{Math.round(combo.avgOrderValue)}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Profitability</Typography>
                         <Chip
                           label={combo.profitability}
                           size="small"
                           sx={{
                             bgcolor: combo.profitability === 'HIGH' ? 'success.main' : combo.profitability === 'MEDIUM' ? 'warning.main' : 'info.main',
                             color: 'white'
                           }}
                         />
                       </Box>
                     </Box>
                     <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                       💡 <strong>AI Recommendation:</strong> Create a bundle offer at ₹{Math.round(combo.avgOrderValue * 0.9)} (10% discount) - could generate ₹{Math.round(combo.avgOrderValue * combo.frequency * 0.3)} additional monthly revenue
                     </Typography>
                   </Box>
                 ))}
               </Box>
             )}

             {/* Price Optimization Recommendations */}
             {businessInsights.personalizedInsights.priceOptimization?.length > 0 && (
               <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(156, 39, 176, 0.05)', borderRadius: 3, border: '1px solid rgba(156, 39, 176, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                   <MonetizationOn sx={{ fontSize: 20 }} />
                   AI Price Optimization - Smart Pricing Strategies
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                   AI-powered price recommendations based on demand, stock levels, and market analysis.
                 </Typography>

                 {businessInsights.personalizedInsights.priceOptimization.slice(0, 4).map((optimization, index) => (
                   <Box key={index} sx={{ mb: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                       <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                         💰 {optimization.productName}
                       </Typography>
                       <Chip
                         label={`${optimization.confidence} CONFIDENCE`}
                         size="small"
                         color="secondary"
                       />
                     </Box>

                     <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Current Price</Typography>
                         <Typography variant="h6" sx={{ color: 'text.primary' }}>₹{optimization.currentPrice}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Suggested Price</Typography>
                         <Typography variant="h6" sx={{
                           color: optimization.action === 'INCREASE' ? 'success.main' : 'warning.main',
                           fontWeight: 600
                         }}>
                           ₹{optimization.suggestedPrice}
                         </Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Action</Typography>
                         <Chip
                           label={optimization.action.replace('_', ' ')}
                           size="small"
                           sx={{
                             bgcolor: optimization.action === 'INCREASE' ? 'success.main' :
                                      optimization.action === 'DECREASE' ? 'warning.main' : 'info.main',
                             color: 'white'
                           }}
                         />
                       </Box>
                     </Box>

                     <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                       <strong>Reason:</strong> {optimization.reason}
                     </Typography>
                     <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                       <strong>Expected Impact:</strong> {optimization.expectedImpact}
                       {optimization.condition && ` (${optimization.condition})`}
                     </Typography>
                   </Box>
                 ))}
               </Box>
             )}

             {/* Bundle Recommendations */}
             {businessInsights.personalizedInsights.bundleRecommendations?.length > 0 && (
               <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                   <Inventory sx={{ fontSize: 20 }} />
                   AI Bundle Recommendations - Ready-to-Sell Packages
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                   AI-generated bundle suggestions based on customer buying patterns and product compatibility.
                 </Typography>

                 {businessInsights.personalizedInsights.bundleRecommendations.slice(0, 3).map((bundle, index) => (
                   <Box key={index} sx={{ mb: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                     <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main', mb: 2 }}>
                       📦 {bundle.type.replace('_', ' ')}: {bundle.products.join(' + ')}
                     </Typography>

                     <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Individual Price</Typography>
                         <Typography variant="h6" sx={{ color: 'text.primary', textDecoration: 'line-through' }}>
                           ₹{bundle.individualPrice}
                         </Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Bundle Price</Typography>
                         <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                           ₹{bundle.bundlePrice}
                         </Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>You Save</Typography>
                         <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                           {bundle.discount}% OFF
                         </Typography>
                       </Box>
                     </Box>

                     <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                       <strong>Target Customers:</strong> {bundle.targetCustomers}
                     </Typography>
                     <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, mb: 1 }}>
                       <strong>Expected Sales Increase:</strong> {bundle.expectedSales}
                     </Typography>
                     <Typography variant="body2" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                       💡 <strong>Marketing Message:</strong> {bundle.marketingAngle}
                     </Typography>
                   </Box>
                 ))}
               </Box>
             )}

             {/* Cross-Selling Opportunities */}
             {businessInsights.personalizedInsights.crossSellingOpportunities?.length > 0 && (
               <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'info.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                   <People sx={{ fontSize: 20 }} />
                   AI Cross-Selling Opportunities - Increase Sales Per Customer
                 </Typography>
                 <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                   AI-identified opportunities to sell additional products to existing customers based on their buying patterns.
                 </Typography>

                 {businessInsights.personalizedInsights.crossSellingOpportunities.slice(0, 3).map((opportunity, index) => (
                   <Box key={index} sx={{ mb: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                       <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
                         🎯 Cross-Sell Opportunity
                       </Typography>
                       <Chip
                         label={`${opportunity.confidence} CONFIDENCE`}
                         size="small"
                         color="info"
                       />
                     </Box>

                     <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                       When customers buy <strong>{opportunity.primaryProduct}</strong>, suggest <strong>{opportunity.suggestedProduct}</strong>
                     </Typography>

                     <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Potential Customers</Typography>
                         <Typography variant="h6" sx={{ color: 'info.main' }}>{opportunity.potentialCustomers}</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Opportunity Score</Typography>
                         <Typography variant="h6" sx={{ color: 'info.main' }}>{opportunity.opportunityScore}%</Typography>
                       </Box>
                       <Box>
                         <Typography variant="body2" sx={{ color: 'text.secondary' }}>Expected Revenue</Typography>
                         <Typography variant="h6" sx={{ color: 'success.main' }}>
                           ₹{(() => {
                             const raw = String(opportunity.expectedRevenue || '');
                             const numeric = parseFloat(raw.replace(/[^0-9.]/g, '')) || 0;
                             return Math.round(numeric).toLocaleString('en-IN');
                           })()}
                         </Typography>
                       </Box>
                     </Box>

                     <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                       💡 <strong>Implementation:</strong> {opportunity.recommendation}
                     </Typography>
                   </Box>
                 ))}
               </Box>
             )}
           </CardContent>
         </Card>
       )}

       {/* Final Summary & Next Steps */}
       {comprehensiveInsights.businessHealth?.status !== 'NOT_LOADED' && (
         <Card sx={{ display: 'none', mb: 6, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
           <CardContent sx={{ p: 4 }}>
             <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
               <SmartToy sx={{ fontSize: 24 }} />
               Final Summary - Your Business at a Glance
             </Typography>

             <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4, mb: 4 }}>
               <Box>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                   🎉 Your Business Strengths:
                 </Typography>
                 <Box sx={{ pl: 2 }}>
                   <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                     • You're making consistent profits - your business model works!
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                     • Your customers are loyal and keep coming back
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                     • Your inventory management is efficient
                   </Typography>
                   <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                     • Your operations are running smoothly
                   </Typography>
                 </Box>
               </Box>

               <Box>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                   📈 Growth Opportunities:
                 </Typography>
                 <Box sx={{ pl: 2 }}>
                   <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                     • Focus on your best-selling products
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                     • Build stronger relationships with top customers
                   </Typography>
                   <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.9)' }}>
                     • Optimize inventory for popular items
                   </Typography>
                   <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                     • Consider expanding your product range
                   </Typography>
                 </Box>
               </Box>
             </Box>

             <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'white' }}>
                 🚀 Your Next Steps:
               </Typography>
               <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                 <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                   <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                     This Week:
                   </Typography>
                   <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                     Focus on your top customers and best-selling products
                   </Typography>
                 </Box>
                 <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                   <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                     This Month:
                   </Typography>
                   <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                     Implement one improvement from the suggestions above
                   </Typography>
                 </Box>
                 <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                   <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                     This Quarter:
                   </Typography>
                   <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                     Plan your business expansion strategy
                   </Typography>
                 </Box>
               </Box>
             </Box>

             <Box sx={{ textAlign: 'center', mt: 4 }}>
               <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 3 }}>
                 💡 <strong>Remember:</strong> Every successful business started small. Focus on your strengths, learn from the data, and keep improving one step at a time.
               </Typography>
               <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                 Your business is on the right track! 🎯
               </Typography>
             </Box>
           </CardContent>
         </Card>
       )}

       {/* Tabs for different insights */}
       <Paper sx={{ mb: 4 }}>
         <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="fullWidth">
           <Tab icon={<Assessment />} label="KPIs" />
           <Tab icon={<Security />} label="Risks" />
           <Tab icon={<Lightbulb />} label="Recommendations" />
           <Tab icon={<Timeline />} label="Analytics" />
         </Tabs>

         {/* KPIs Tab */}
         {activeTab === 0 && (
           <Box p={3}>
             <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
               Key Performance Indicators
             </Typography>

             <Grid container spacing={3}>
               {/* Sales KPIs */}
               <Grid item xs={12} md={6}>
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                       <MonetizationOn sx={{ mr: 1 }} />
                       Sales Performance
                     </Typography>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                       <Typography variant="h6">{formatCurrency(crossSheetInsights.keyMetrics?.totalRevenue || crossSheetInsights.keyMetrics?.totalSales || 0)}</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Average Order Value</Typography>
                       <Typography variant="h6">{formatCurrency(crossSheetInsights.keyMetrics?.averageOrderValue || crossSheetInsights.keyMetrics?.avgOrderValue || 0)}</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Sales Growth Rate</Typography>
                       <Typography variant="h6" sx={{ color: (crossSheetInsights.keyMetrics?.salesGrowthRate || 0) >= 0 ? 'success.main' : 'error.main' }}>
                         {(crossSheetInsights.keyMetrics?.salesGrowthRate || 0) >= 0 ? '+' : ''}
                         {formatPercent(crossSheetInsights.keyMetrics?.salesGrowthRate || 0)}
                       </Typography>
                     </Box>
                   </CardContent>
                 </Card>
               </Grid>

               {/* Financial KPIs */}
               <Grid item xs={12} md={6}>
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                       <TrendingUp sx={{ mr: 1 }} />
                       Financial Health
                     </Typography>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Gross Margin</Typography>
                       <Typography variant="h6">{formatPercent(crossSheetInsights.keyMetrics?.grossMargin || crossSheetInsights.keyMetrics?.profitMargin || 0)}</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Net Profit Margin</Typography>
                       <Typography variant="h6">{formatPercent(crossSheetInsights.keyMetrics?.netProfitMargin || crossSheetInsights.keyMetrics?.profitMargin || 0)}</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Working Capital Ratio</Typography>
                       <Typography variant="h6">{(crossSheetInsights.keyMetrics?.workingCapitalRatio || crossSheetInsights.keyMetrics?.customerOutstandingRatio || 0).toFixed(2)}</Typography>
                     </Box>
                   </CardContent>
                 </Card>
               </Grid>

               {/* Customer KPIs */}
               <Grid item xs={12} md={6}>
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                       <People sx={{ mr: 1 }} />
                       Customer Metrics
                     </Typography>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Customer Lifetime Value</Typography>
                       <Typography variant="h6">{formatCurrency(crossSheetInsights.keyMetrics?.customerLifetimeValue || crossSheetInsights.keyMetrics?.averageOrderValue || 0)}</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Churn Rate</Typography>
                       <Typography variant="h6">{formatPercent((crossSheetInsights.keyMetrics?.churnRate || 0) * 100)}</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Repeat Purchase Rate</Typography>
                       <Typography variant="h6">{formatPercent((crossSheetInsights.keyMetrics?.repeatPurchaseRate || 0) * 100)}</Typography>
                     </Box>
                   </CardContent>
                 </Card>
               </Grid>

               {/* Operational KPIs */}
               <Grid item xs={12} md={6}>
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                       <Inventory sx={{ mr: 1 }} />
                       Operational Efficiency
                     </Typography>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Inventory Turnover</Typography>
                       <Typography variant="h6">{(crossSheetInsights.keyMetrics?.inventoryTurnoverRatio || crossSheetInsights.keyMetrics?.inventoryTurnover || 0).toFixed(1)}x</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Order Fulfillment Time</Typography>
                       <Typography variant="h6">{crossSheetInsights.keyMetrics?.orderFulfillmentTime || crossSheetInsights.keyMetrics?.fulfillmentTime || 0} days</Typography>
                     </Box>
                     <Box sx={{ mb: 2 }}>
                       <Typography variant="body2" color="text.secondary">Process Efficiency</Typography>
                       <Typography variant="h6">{formatPercent(crossSheetInsights.keyMetrics?.processEfficiency || crossSheetInsights.keyMetrics?.efficiency || 0)}</Typography>
                     </Box>
                   </CardContent>
                 </Card>
               </Grid>
             </Grid>
           </Box>
         )}

         {/* Risks Tab */}
         {activeTab === 1 && (
           <Box p={3}>
             <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
               Risk Assessments
             </Typography>

             {comprehensiveInsights.riskAssessments?.length > 0 ? (
               <Grid container spacing={2}>
                 {comprehensiveInsights.riskAssessments.map((risk, index) => (
                   <Grid item xs={12} md={6} key={index}>
                     <Card sx={{ borderLeft: 4, borderLeftColor: risk.severity === 'HIGH' ? 'error.main' : risk.severity === 'MEDIUM' ? 'warning.main' : 'info.main' }}>
                       <CardContent>
                         <Typography variant="h6" gutterBottom>{risk.title}</Typography>
                         <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                           {risk.description}
                         </Typography>
                         <Box display="flex" gap={1} mb={2}>
                           <Chip label={`Impact: ${risk.impact}`} size="small" color={risk.impact === 'HIGH' ? 'error' : 'default'} />
                           <Chip label={`Probability: ${risk.probability}`} size="small" color={risk.probability === 'HIGH' ? 'error' : 'default'} />
                         </Box>
                         <Typography variant="body2" sx={{ fontWeight: 500 }}>
                           Mitigation: {risk.mitigation}
                         </Typography>
                       </CardContent>
                     </Card>
                   </Grid>
                 ))}
               </Grid>
             ) : (
               <Alert severity="success">No significant risks detected in your current operations.</Alert>
             )}
           </Box>
         )}

         {/* Recommendations Tab */}
         {activeTab === 2 && (
           <Box p={3}>
             <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
               Strategic Recommendations
             </Typography>

             {comprehensiveInsights.strategicRecommendations?.length > 0 ? (
               <List>
                 {comprehensiveInsights.strategicRecommendations.map((rec, index) => (
                   <Accordion key={index} sx={{ mb: 1 }}>
                     <AccordionSummary expandIcon={<ExpandMore />}>
                       <Box display="flex" alignItems="center" width="100%">
                         <Chip
                           label={rec.category}
                           color={rec.priority === 'HIGH' ? 'error' : rec.priority === 'MEDIUM' ? 'warning' : 'info'}
                           size="small"
                           sx={{ mr: 2 }}
                         />
                         <Typography variant="h6">{rec.title}</Typography>
                       </Box>
                     </AccordionSummary>
                     <AccordionDetails>
                       <Typography variant="body1" sx={{ mb: 2 }}>
                         {rec.description}
                       </Typography>

                       {rec.actions && rec.actions.length > 0 && (
                         <Box sx={{ mb: 2 }}>
                           <Typography variant="h6" gutterBottom>Recommended Actions:</Typography>
                           <List dense>
                             {rec.actions.map((action, actionIndex) => (
                               <ListItem key={actionIndex}>
                                 <ListItemIcon>
                                   <CheckCircle color="success" />
                                 </ListItemIcon>
                                 <ListItemText primary={action} />
                               </ListItem>
                             ))}
                           </List>
                         </Box>
                       )}

                       {rec.expectedImpact && (
                         <Typography variant="body2" color="text.secondary">
                           <strong>Expected Impact:</strong> {rec.expectedImpact}
                         </Typography>
                       )}

                       {rec.timeframe && (
                         <Typography variant="body2" color="text.secondary">
                           <strong>Timeframe:</strong> {rec.timeframe}
                         </Typography>
                       )}
                     </AccordionDetails>
                   </Accordion>
                 ))}
               </List>
             ) : (
               <Alert severity="info">No specific recommendations available at this time.</Alert>
             )}
           </Box>
         )}

         {/* Analytics Tab */}
         {activeTab === 3 && (
           <Box p={3}>
             <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
               Advanced Analytics
             </Typography>

             <Grid container spacing={3}>
               {/* Growth Opportunities */}
               <Grid item xs={12} md={6}>
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                       <Lightbulb sx={{ mr: 1 }} />
                       Growth Opportunities
                     </Typography>

                     {comprehensiveInsights.growthOpportunities?.length > 0 ? (
                       <List dense>
                         {comprehensiveInsights.growthOpportunities.slice(0, 3).map((opp, index) => (
                           <ListItem key={index}>
                             <ListItemText
                               primary={opp.title}
                               secondary={`${opp.type} • ${opp.difficulty} difficulty`}
                             />
                           </ListItem>
                         ))}
                       </List>
                     ) : (
                       <Typography variant="body2" color="text.secondary">
                         No specific growth opportunities identified.
                       </Typography>
                     )}
                   </CardContent>
                 </Card>
               </Grid>

               {/* Predictive Analytics */}
               <Grid item xs={12} md={6}>
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                       <Timeline sx={{ mr: 1 }} />
                       Predictive Insights
                     </Typography>

                     <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                       Based on current trends and patterns in your data.
                     </Typography>

                     {comprehensiveInsights.predictiveAnalytics?.salesForecast && (
                       <Box sx={{ mb: 2 }}>
                         <Typography variant="body2" color="text.secondary">Sales Forecast</Typography>
                         <Typography variant="h6" color="primary">
                           Next month: {formatCurrency(comprehensiveInsights.predictiveAnalytics.salesForecast.nextMonth || 0)}
                         </Typography>
                       </Box>
                     )}

                     <Typography variant="body2" color="text.secondary">
                       Advanced predictive analytics will be available as more data is collected.
                     </Typography>
                   </CardContent>
                 </Card>
               </Grid>
             </Grid>
           </Box>
         )}
       </Paper>
     </Box>
   </Box>
 );
}

export default BusinessIntelligence;
